<!--
  DiagReportPanel.vue
  콜봇 월 정산 · 연결 진단 탭의 2-5 "병원 리포트 열람". 읽기 전용, 돈 없음.

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-5", 응답 모양은
        ~/md/front/콜봇-월간이용리포트-프론트요청.md "요청 계약"(GET .../callbot/stats/monthly)
  - 병원이 studio "월간 이용 리포트"에서 보는 것과 같은 데이터다. 응답에 원가·요금 필드가 없다(6차 개정).
  - 요약 숫자: 걸려온 전화(real_calls) · AI 가 받은 전화 · AI 응대 시간 · 직원 연결 요청(handoffs) · 응답 · 미응답 ·
    직원 통화 시간(time.counselor_talk_seconds) · 응답률(answered / handoffs, 요청 0 이면 '—').
  - 번호별 응답 표: per_number[](번호는 끝 4자리만) + 번호에 귀속 못 한 응답(per_number_unattributed).
  - 미응답 목록(unanswered.items, 최신순·최대 1,000건): 수신 시각 · 대기 · "처음 연결한 번호"(특정 직원 탓으로
    읽히지 않게 이 이름을 쓴다) · 통화 ID. 행을 누르면 'lookup'(vox_call_id)을 올려 통화 1건 연결 확인을 연다.
  데이터는 부모(DiagTab)가 받은 리포트 응답 하나다.
-->
<template>
  <section class="report-card" :aria-labelledby="titleId">
    <header class="report-card__head">
      <h2 :id="titleId" class="report-card__title">병원 리포트 열람</h2>
      <span class="report-card__meta">병원이 studio '월간 이용 리포트'에서 보는 것과 같은 숫자 · 읽기 전용</span>
      <div v-if="$slots['header-extra']" class="report-card__extra">
        <slot name="header-extra" />
      </div>
    </header>

    <div class="report-kpis">
      <div v-for="kpi in kpis" :key="kpi.key" class="report-kpi">
        <span class="report-kpi__label">{{ kpi.label }}</span>
        <span class="report-kpi__value">{{ kpi.value }}</span>
        <span v-if="kpi.note" class="report-kpi__note">{{ kpi.note }}</span>
      </div>
    </div>

    <div class="report-grid">
      <!-- 번호별 응답 -->
      <div class="report-block">
        <div class="report-block__head">
          <h3 class="report-block__title">번호별 응답</h3>
          <span class="report-block__meta">전달하기로 넘어간 통화는 최종으로 받은 번호에 셉니다</span>
        </div>
        <div class="report-table-wrap">
          <table class="report-table">
            <thead>
              <tr>
                <th scope="col">번호</th>
                <th scope="col">이름</th>
                <th scope="col" class="is-num">응답</th>
                <th scope="col" class="is-num">직원 통화 시간</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!numberRows.length">
                <td colspan="4" class="report-table__empty">등록된 상담사 번호가 없습니다.</td>
              </tr>
              <tr v-for="row in numberRows" :key="row.key" :class="{ 'is-zero': row.answered === 0 }">
                <td class="report-table__number">{{ row.number }}</td>
                <td>{{ row.name }}</td>
                <td class="is-num">{{ formatCount(row.answered, '통') }}</td>
                <td class="is-num">{{ formatDuration(row.talkSeconds) }}</td>
              </tr>
              <tr v-if="unattributed" class="is-unattributed">
                <td colspan="2">번호에 귀속 못 한 응답</td>
                <td class="is-num">{{ formatCount(unattributed.answered, '통') }}</td>
                <td class="is-num">{{ formatDuration(unattributed.talkSeconds) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="numberRows.length || unattributed">
              <tr>
                <th scope="row" colspan="2">합계</th>
                <td class="is-num">{{ formatCount(numberTotals.answered, '통') }}</td>
                <td class="is-num">{{ formatDuration(numberTotals.talkSeconds) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- 미응답 목록 -->
      <div class="report-block">
        <div class="report-block__head">
          <h3 class="report-block__title">직원이 받지 못한 전화 {{ formatCount(unanswered.count, '통') }}</h3>
          <span v-if="unanswered.waitSummary" class="report-block__meta">{{ unanswered.waitSummary }}</span>
        </div>
        <p v-if="unanswered.bucketText" class="report-block__line">{{ unanswered.bucketText }}</p>
        <p v-if="unanswered.truncated" class="report-block__line is-warn">
          최신 {{ formatNumber(unanswered.rows.length) }}건만 보입니다 — 그보다 오래된 미응답은 목록에서 빠졌습니다.
        </p>
        <a-table
          v-if="unanswered.rows.length"
          class="report-unanswered"
          size="small"
          row-key="key"
          :columns="UNANSWERED_COLUMNS"
          :data-source="unanswered.rows"
          :pagination="false"
          :custom-row="unansweredRowProps"
          :row-class-name="unansweredRowClass"
        >
          <template #bodyCell="{ column, record }">
            <span v-if="column.key === 'wait'" :title="record.waitTitle">{{ record.wait }}</span>
            <span v-else-if="column.key === 'target'" :class="{ 'is-muted': !record.hasTarget }">
              {{ record.target }}
            </span>
            <button
              v-else-if="column.key === 'id'"
              type="button"
              class="report-link"
              :title="`${record.id} — 연결 확인`"
              @click.stop="emit('lookup', record.id)"
            >
              {{ record.shortId }}
            </button>
          </template>
        </a-table>
        <p v-else class="report-block__empty">이 달 직원이 받지 못한 전화가 없습니다.</p>
        <p v-if="unanswered.rows.length" class="report-block__hint">
          행을 누르면 아래 "통화 1건 연결 확인"에서 그 통화의 연결 기록을 봅니다.
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';

import {
  EMPTY,
  formatCount,
  formatDuration,
  formatKstDateTime,
  formatNumber,
  isNumber,
  maskNumber,
} from '../../service/settlementFormat';

const props = defineProps({
  /** 월간 이용 리포트 응답(GET .../callbot/stats/monthly) */
  stats: { type: Object, required: true },
  /** 통화 1건 연결 확인에 열려 있는 통화 ID — 미응답 행 강조 */
  selectedCallId: { type: String, default: '' },
});

const emit = defineEmits(['lookup']);

const UNANSWERED_COLUMNS = [
  { title: '수신 시각', key: 'at', dataIndex: 'at' },
  { title: '대기', key: 'wait', dataIndex: 'wait', align: 'right' },
  { title: '처음 연결한 번호', key: 'target', dataIndex: 'target' },
  { title: '통화 ID', key: 'id', dataIndex: 'shortId' },
];

const titleId = `st-diag-report-${getCurrentInstance()?.uid ?? 0}`;

const num = (v) => (isNumber(v) ? v : null);
const arr = (v) => (Array.isArray(v) ? v : []);

/** n / d 를 소수 한 자리 퍼센트로. 분모가 0 이거나 없으면 '—' */
function percent(n, d) {
  if (!isNumber(n) || !isNumber(d) || d <= 0) return EMPTY;
  return `${(Math.round((n / d) * 1000) / 10).toFixed(1)}%`;
}

const kpis = computed(() => {
  const summary = props.stats?.summary ?? {};
  const flow = props.stats?.flow ?? {};
  const time = props.stats?.time ?? {};
  const answered = num(flow.answered);
  const handoffs = num(flow.handoffs);
  const talk = num(time.counselor_talk_seconds);

  const realNotes = [`웹 테스트 ${formatCount(summary.web_calls, '통')} 제외`];
  if (num(summary.canceled_before_ai) > 0) {
    realNotes.push(`AI 가 받기 전 끊긴 ${formatNumber(summary.canceled_before_ai)}통 포함`);
  }
  if (num(summary.outbound_calls) > 0) realNotes.push(`발신 ${formatNumber(summary.outbound_calls)}통 포함`);

  const handoffNotes = [];
  if (num(flow.vox_direct) > 0) handoffNotes.push(`vox 직접 ${formatNumber(flow.vox_direct)}통 포함`);
  if (num(flow.in_progress) > 0) handoffNotes.push(`진행 중 ${formatNumber(flow.in_progress)}통`);

  const avgAi = num(summary.ai_avg_seconds);
  const withoutTarget = num(flow.unanswered_without_target);

  return [
    { key: 'real', label: '걸려온 전화', value: formatCount(summary.real_calls, '통'), note: realNotes.join(' · ') },
    { key: 'ai', label: 'AI 가 받은 전화', value: formatCount(summary.ai_answered, '통'), note: 'AI 응답 시각이 있는 통화' },
    {
      key: 'aiTime',
      label: 'AI 응대 시간',
      value: formatDuration(summary.ai_seconds),
      note: avgAi !== null ? `평균 ${formatDuration(avgAi)} · 직원 연결 구간 제외` : '직원 연결 구간 제외',
    },
    { key: 'handoffs', label: '직원 연결 요청', value: formatCount(handoffs, '통'), note: handoffNotes.join(' · ') },
    { key: 'answered', label: '직원 응답', value: formatCount(answered, '통'), note: '연결 시각이 있거나 통화 시간이 0초보다 큼' },
    {
      key: 'unanswered',
      label: '직원 미응답',
      value: formatCount(flow.unanswered, '통'),
      note: withoutTarget > 0 ? `연결 대상 없음 ${formatNumber(withoutTarget)}통 포함(운영시간 외 등)` : '',
    },
    {
      key: 'talk',
      label: '직원 통화 시간',
      value: formatDuration(talk),
      note: talk !== null && answered > 0 ? `응답 1통 평균 ${formatDuration(Math.round(talk / answered))}` : '',
    },
    {
      key: 'rate',
      label: '직원 응답률',
      value: percent(answered, handoffs),
      note: `응답 ${formatNumber(answered)} / 연결 요청 ${formatNumber(handoffs)}`,
    },
  ];
});

// ---------------------------------------------------------------------------
// 번호별 응답
// ---------------------------------------------------------------------------

const numberRows = computed(() =>
  arr(props.stats?.per_number).map((row, i) => ({
    key: row?.counselor_number_id ?? `n${i}`,
    number: maskNumber(row?.number),
    name: typeof row?.name === 'string' && row.name.trim() ? row.name : EMPTY,
    answered: num(row?.answered),
    talkSeconds: num(row?.talk_seconds),
  }))
);

/** 번호 행에 귀속하지 못한 응답 — 0 이면 줄을 만들지 않는다 */
const unattributed = computed(() => {
  const u = props.stats?.per_number_unattributed;
  const answered = num(u?.answered) ?? 0;
  const talkSeconds = num(u?.talk_seconds) ?? 0;
  return answered > 0 || talkSeconds > 0 ? { answered, talkSeconds } : null;
});

const numberTotals = computed(() => {
  const rows = [...numberRows.value, ...(unattributed.value ? [unattributed.value] : [])];
  return {
    answered: rows.reduce((sum, r) => sum + (r.answered ?? 0), 0),
    talkSeconds: rows.reduce((sum, r) => sum + (r.talkSeconds ?? 0), 0),
  };
});

// ---------------------------------------------------------------------------
// 미응답 목록
// ---------------------------------------------------------------------------

const unanswered = computed(() => {
  const block = props.stats?.unanswered ?? {};
  const buckets = block.wait_buckets ?? null;
  const avg = num(block.avg_wait_seconds);
  const rows = arr(block.items).map((item, i) => {
    const wait = num(item?.wait_seconds);
    const target = item?.first_target_number;
    const id = typeof item?.vox_call_id === 'string' ? item.vox_call_id : '';
    return {
      key: id || `u${i}`,
      id,
      shortId: id ? `${id.slice(0, 8)}…` : EMPTY,
      at: formatKstDateTime(item?.received_at),
      wait: wait !== null ? formatDuration(wait) : EMPTY,
      waitTitle: wait !== null ? '' : 'vox 직접 연결은 대기를 재지 않습니다',
      hasTarget: !!target,
      target: target ? maskNumber(target) : '연결 대상 없음',
    };
  });
  const bucketText =
    buckets && typeof buckets === 'object'
      ? [
          `기다린 시간: 10초 미만 ${formatNumber(buckets.lt10)}`,
          `10–30초 ${formatNumber(buckets.s10_30)}`,
          `30–60초 ${formatNumber(buckets.s30_60)}`,
          `60초 이상 ${formatNumber(buckets.gte60)}`,
        ].join(' · ')
      : '';
  return {
    count: num(props.stats?.flow?.unanswered) ?? rows.length,
    rows,
    truncated: block.items_truncated === true,
    waitSummary: avg !== null ? `평균 대기 ${formatDuration(avg)}(vox 직접 제외)` : '',
    bucketText,
  };
});

function unansweredRowProps(record) {
  return {
    onClick: () => {
      if (record.id) emit('lookup', record.id);
    },
  };
}

function unansweredRowClass(record) {
  return record.id && record.id === props.selectedCallId ? 'is-selected' : '';
}
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.report-card {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px 10px;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: $st-text;
  }

  &__meta {
    font-size: 12px;
    color: $st-muted;
  }

  &__extra {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
}

// 요약 여덟 칸 — 넓은 화면에서 네 칸씩 두 줄
.report-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 10px;
}

.report-kpi {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  border: 1px solid $st-border;
  border-radius: 10px;

  &__label {
    font-size: 12px;
    color: $st-text-secondary;
  }

  &__value {
    @include st-num;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
    color: $st-text;
  }

  &__note {
    font-size: 11.5px;
    color: $st-muted;
  }
}

.report-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 380px), 1fr));
  gap: 14px;
  align-items: start;
}

.report-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
  }

  &__title {
    margin: 0;
    font-size: 13.5px;
    font-weight: 700;
    color: $st-text;
  }

  &__meta,
  &__line,
  &__hint {
    margin: 0;
    font-size: 12px;
    color: $st-muted;
  }

  &__line.is-warn {
    color: $st-warning;
  }

  &__empty {
    margin: 0;
    padding: 12px;
    border-radius: 8px;
    background: $st-bg;
    font-size: 12.5px;
    color: $st-muted;
  }
}

.report-table-wrap {
  overflow-x: auto;
  border: 1px solid $st-border;
  border-radius: 10px;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  th,
  td {
    padding: 8px 12px;
    // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
    border-bottom: 1px solid $st-border !important;
    text-align: left;
    white-space: nowrap;
  }

  thead th {
    background: $st-bg;
    font-size: 12px;
    font-weight: 600;
    color: $st-text-secondary;
  }

  .is-num {
    @include st-num;
    text-align: right;
  }

  tbody tr.is-zero td {
    color: $st-muted;
  }

  tbody tr.is-unattributed td {
    background: $st-warning-bg;
    color: $st-text-secondary;
  }

  tfoot th,
  tfoot td {
    border-bottom: 0 !important;
    background: $st-bg;
    font-weight: 700;
    color: $st-text;
  }

  &__number {
    @include st-num;
    font-weight: 600;
    color: $st-text;
  }

  &__empty {
    color: $st-muted;
    text-align: center !important;
  }
}

.report-unanswered {
  :deep(.ant-table) {
    font-size: 12.5px;
  }

  :deep(.ant-table-row) {
    cursor: pointer;
  }

  :deep(.ant-table-cell) {
    @include st-num;
  }

  :deep(.ant-table-row.is-selected > td) {
    background: $st-accent-bg;
  }

  .is-muted {
    color: $st-muted;
  }
}

.report-link {
  height: auto; // 전역 button { height: 40px } 덮어쓰기 — 없으면 표 줄이 두꺼워진다
  padding: 0;
  border: 0;
  background: none;
  color: $st-accent;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $st-accent;
    outline-offset: 2px;
  }
}
</style>
